import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { BASE_URL } from '../config';
import Webcam from 'react-webcam';
import QrScanner from 'qr-scanner';

const MfaAuthenticator = () => {
  const [mfaCodes, setMfaCodes] = useState({});
  const [localCountdowns, setLocalCountdowns] = useState({}); // Local countdown state for smooth animation
  // eslint-disable-next-line no-unused-vars
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('qr'); // 'qr' or 'manual'
  const [isScanning, setIsScanning] = useState(false);
  const [newMfa, setNewMfa] = useState({
    name: '',
    username: '',
    otp_url: '',
    otp_code: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showQrConfirmation, setShowQrConfirmation] = useState(false);
  // File upload states
  const [isProcessingFile, setIsProcessingFile] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [pasteActive, setPasteActive] = useState(false);
  // Filtering states
  const [selectedPortal, setSelectedPortal] = useState('');
  const [usernameFilter, setUsernameFilter] = useState('');
  // Portal search states
  const [portalSearch, setPortalSearch] = useState('');
  const [isPortalDropdownOpen, setIsPortalDropdownOpen] = useState(false);
  const { apiKey } = useAuth();
  const webcamRef = useRef(null);
  const scannerRef = useRef(null);
  const refreshIntervalRef = useRef(null);
  const countdownIntervalRef = useRef(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Initial fetch
    fetchMfaCodes();
    
    // Check if QrScanner is available
    console.log('QrScanner available:', typeof QrScanner);
    
    // Calculate time until next 15-second marker (:15, :30, :45, :00)
    const now = new Date();
    const currentSeconds = now.getSeconds();
    const nextMarker = Math.ceil((currentSeconds + 1) / 15) * 15;
    const secondsUntilNextMarker = nextMarker === 60 ? 60 - currentSeconds : nextMarker - currentSeconds;
    
    console.log(`Current time: ${currentSeconds}s, next refresh in ${secondsUntilNextMarker}s`);
    
    // Set timeout to sync with 15-second markers
    const initialTimeout = setTimeout(() => {
      fetchMfaCodes();
      console.log('Synced to 15-second marker, starting interval');
      
      // Now set up regular 15-second interval
      refreshIntervalRef.current = setInterval(() => {
        const now = new Date();
        console.log(`Refreshing MFA codes at ${now.getSeconds()}s`);
        fetchMfaCodes();
      }, 15000);
    }, secondsUntilNextMarker * 1000);
    
    // Set up 1-second countdown interval for smooth animation
    countdownIntervalRef.current = setInterval(() => {
      setLocalCountdowns(prev => {
        const updated = {};
        Object.entries(prev).forEach(([key, data]) => {
          const newSecondsRemaining = data.seconds_remaining - 1;
          // Reset to 30 when it hits 0 (TOTP codes reset every 30 seconds)
          updated[key] = {
            ...data,
            seconds_remaining: newSecondsRemaining <= 0 ? 30 : newSecondsRemaining
          };
        });
        return updated;
      });
    }, 1000);

    return () => {
      clearTimeout(initialTimeout);
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keyboard shortcuts for better UX
  useEffect(() => {
    const handleKeyPress = (e) => {
      // Clear filters on Escape key
      if (e.key === 'Escape' && (selectedPortal || usernameFilter || portalSearch)) {
        setSelectedPortal('');
        setUsernameFilter('');
        setPortalSearch('');
        setIsPortalDropdownOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [selectedPortal, usernameFilter, portalSearch]);



  const fetchMfaCodes = async () => {
    if (!apiKey?.key) return;
    
    try {
      const response = await fetch(`${BASE_URL}/mfa/all`, {
        headers: {
          'Authorization': `Bearer ${apiKey.key}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        
        // Filter out _failed_entries from the main data
        const { _failed_entries, ...cleanData } = data;
        setMfaCodes(cleanData);
        
        // Show warning if there are failed entries
        if (_failed_entries && _failed_entries.length > 0) {
          console.warn('Some MFA entries failed to load:', _failed_entries);
          setError(`Warning: ${_failed_entries.length} MFA entries failed to load due to invalid configuration. Check console for details.`);
        } else {
          setError(null);
        }
        
        // Update local countdowns with fresh data from API
        const newCountdowns = {};
        Object.entries(cleanData).forEach(([key, mfaData]) => {
          // Only process valid MFA data (skip _failed_entries)
          if (mfaData.portal_name && mfaData.code !== undefined) {
            newCountdowns[key] = {
              portal_name: mfaData.portal_name,
              code: mfaData.code,
              seconds_remaining: mfaData.seconds_remaining,
              username: mfaData.username
            };
          }
        });
        setLocalCountdowns(newCountdowns);
      } else {
        const errorData = await response.json();
        setError(typeof errorData.detail === 'string' ? errorData.detail : 'Failed to fetch MFA codes');
      }
    } catch (error) {
      console.error('Error fetching MFA codes:', error);
      setError('Failed to fetch MFA codes. Please check your connection.');
    }
  };

  const startQrScan = async () => {
    setIsScanning(true);
    setError(null);
    
    try {
      // Wait a bit for the webcam to be ready
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      if (webcamRef.current && webcamRef.current.video) {
        const video = webcamRef.current.video;
        
        // Wait for video to be ready
        if (video.readyState < 2) {
          await new Promise((resolve) => {
            video.addEventListener('loadeddata', resolve, { once: true });
          });
        }
        
        console.log('Initializing QR scanner...');
        
        // Check if QrScanner is supported
        if (!QrScanner.hasCamera()) {
          throw new Error('No camera found');
        }
        
        // Create QR scanner instance with better options
        scannerRef.current = new QrScanner(
          video, 
          (result) => {
            console.log('QR Code detected:', result);
            handleQrResult(result);
          },
          {
            returnDetailedScanResult: true,
            highlightScanRegion: true,
            highlightCodeOutline: true,
            maxScansPerSecond: 5,
            preferredCamera: 'environment',
            calculateScanRegion: (video) => {
              // Define scan region as center square
              const smallerDimension = Math.min(video.videoWidth, video.videoHeight);
              const scanSize = Math.round(0.7 * smallerDimension);
              
              return {
                x: Math.round((video.videoWidth - scanSize) / 2),
                y: Math.round((video.videoHeight - scanSize) / 2),
                width: scanSize,
                height: scanSize,
              };
            }
          }
        );
        
        console.log('Starting QR scanner...');
        await scannerRef.current.start();
        console.log('QR scanner started successfully');
      } else {
        throw new Error('Camera not available');
      }
    } catch (error) {
      console.error('Error starting QR scanner:', error);
      setError(`Failed to start QR scanner: ${error.message}. Please check camera permissions and try again.`);
      setIsScanning(false);
    }
  };

  const stopQrScan = () => {
    if (scannerRef.current) {
      scannerRef.current.stop();
      scannerRef.current.destroy();
      scannerRef.current = null;
    }
    setIsScanning(false);
  };

  const handleQrResult = useCallback((result) => {
    console.log('Processing QR scan result:', result);
    stopQrScan();
    
    // Handle both simple string results and detailed scan results
    const qrData = typeof result === 'string' ? result : result.data;
    console.log('QR data (raw):', qrData);
    
    // Parse OTP URL format: otpauth://totp/ServiceName:username@example.com?secret=...&issuer=ServiceName
    try {
      const url = new URL(qrData);
      if (url.protocol === 'otpauth:') {
        // Decode the pathname to handle URL-encoded characters like %20, %3A, %40, etc.
        const decodedPathname = decodeURIComponent(url.pathname);
        console.log('Decoded pathname:', decodedPathname);
        
        // Split on ':' after decoding
        const pathParts = decodedPathname.split(':');
        console.log('Path parts:', pathParts);
        
        // Extract service name (remove leading '/')
        const serviceName = pathParts[0].substring(1);
        
        // Extract username - handle multiple colons in case there are multiple parts
        // Format could be: /ServiceName:username or /ServiceName:account:username@domain.com
        let username = '';
        if (pathParts.length > 1) {
          // Join all parts after the service name with ':'
          const userPart = pathParts.slice(1).join(':');
          // Extract username part before '@' if present
          username = userPart.split('@')[0];
        }
        
        // Try to get issuer from URL params as fallback
        const issuer = url.searchParams.get('issuer') || serviceName;
        
        console.log('Parsed QR code:', { 
          serviceName, 
          username, 
          issuer, 
          decodedPathname,
          pathParts 
        });
        
        setNewMfa({
          name: issuer || serviceName || '',
          username: username || '',
          otp_url: qrData,
          otp_code: ''
        });
        setError(null);
        setShowQrConfirmation(true);
      } else {
        console.error('Invalid protocol:', url.protocol);
        setError('Invalid QR code format. Please scan a valid authenticator QR code.');
      }
    } catch (error) {
      console.error('Error parsing QR code:', error, 'Data:', qrData);
      setError('Invalid QR code format. Please scan a valid authenticator QR code.');
    }
  }, []);

  const handleFileUpload = useCallback(async (file) => {
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select a valid image file (PNG, JPG, etc.)');
      return;
    }
    
    setIsProcessingFile(true);
    setError(null);
    
    try {
      console.log('Processing uploaded file:', file.name, file.type);
      
      // Use QrScanner to scan the uploaded image
      const result = await QrScanner.scanImage(file, { 
        returnDetailedScanResult: true 
      });
      
      console.log('QR code found in uploaded image:', result);
      handleQrResult(result);
    } catch (error) {
      console.error('Error processing uploaded image:', error);
      setError('No QR code found in the uploaded image. Please try a different image or ensure the QR code is clearly visible.');
    } finally {
      setIsProcessingFile(false);
    }
  }, [handleQrResult]);

  const handlePaste = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setPasteActive(false);
    
    const items = e.clipboardData?.items;
    if (!items) return;
    
    // Look for image items in clipboard
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) {
          console.log('Image pasted from clipboard:', file.type, file.size);
          await handleFileUpload(file);
          return;
        }
      }
    }
    
    // If no image found, show helpful message
    setError('No image found in clipboard. Try right-clicking on a QR code and selecting "Copy Image".');
  }, [handleFileUpload]);

  // Clipboard paste handling when modal is open
  useEffect(() => {
    if (!isAddModalOpen || activeTab !== 'qr') return;

    const handleGlobalPaste = (e) => {
      // Only handle if we're not in an input field
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      
      handlePaste(e);
    };

    document.addEventListener('paste', handleGlobalPaste);
    return () => document.removeEventListener('paste', handleGlobalPaste);
  }, [isAddModalOpen, activeTab, handlePaste]);

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!dragActive) {
      setDragActive(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const files = e.dataTransfer.files;
    if (files && files[0]) {
      handleFileUpload(files[0]);
    }
  };

  const handleAddMfa = async () => {
    if (!newMfa.name || !newMfa.username || (!newMfa.otp_url && !newMfa.otp_code)) {
      setError('Please fill in all required fields.');
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        name: newMfa.name,
        username: newMfa.username,
        ...(newMfa.otp_url && { otp_url: newMfa.otp_url }),
        ...(newMfa.otp_code && { otp_code: newMfa.otp_code })
      };

      const response = await fetch(`${BASE_URL}/mfa/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${apiKey.key}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setIsAddModalOpen(false);
        setNewMfa({ name: '', username: '', otp_url: '', otp_code: '' });
        setActiveTab('qr');
        fetchMfaCodes();
        setError(null);
        setShowQrConfirmation(false);
      } else {
        const errorData = await response.json();
        setError(typeof errorData.detail === 'string' ? errorData.detail : 'Failed to add MFA');
      }
    } catch (error) {
      console.error('Error adding MFA:', error);
      setError('Failed to add MFA. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatTimeRemaining = (seconds) => {
    return `${seconds}s`;
  };

  const getProgressPercentage = (seconds) => {
    return (seconds / 30) * 100; // Assuming 30-second intervals
  };

  // Simple fuzzy search function
  const fuzzyMatch = (searchTerm, text) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    const target = text.toLowerCase();
    
    // Simple contains match first
    if (target.includes(search)) return true;
    
    // Then try fuzzy matching (characters in order but not necessarily consecutive)
    let searchIndex = 0;
    for (let i = 0; i < target.length && searchIndex < search.length; i++) {
      if (target[i] === search[searchIndex]) {
        searchIndex++;
      }
    }
    return searchIndex === search.length;
  };

  // Get unique portal names for the dropdown
  const getUniquePortals = () => {
    const portals = Object.values(mfaCodes).map(data => data.portal_name);
    return [...new Set(portals)].sort();
  };

  // Get filtered portals for searchable dropdown
  const getFilteredPortals = () => {
    const allPortals = getUniquePortals();
    if (!portalSearch) return allPortals;
    
    return allPortals.filter(portal => 
      fuzzyMatch(portalSearch, portal)
    );
  };

  // Filter MFA codes based on selected portal and username search
  const getFilteredMfaCodes = () => {
    let filtered = Object.entries(mfaCodes);
    
    // Filter by portal if one is selected
    if (selectedPortal) {
      filtered = filtered.filter(([key, data]) => data.portal_name === selectedPortal);
    }
    
    // Filter by username if search term is provided
    if (usernameFilter) {
      filtered = filtered.filter(([key, data]) => {
        const username = localCountdowns[key]?.username || data.username || '';
        return fuzzyMatch(usernameFilter, username);
      });
    }
    
    return Object.fromEntries(filtered);
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 space-y-4 sm:space-y-0">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">MFA Authenticator</h1>
        <button
          onClick={() => setIsAddModalOpen(true)}
          className="w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-center"
        >
          Add New MFA
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">
          {error}
        </div>
      )}

      {/* Filter Controls */}
      {Object.keys(mfaCodes).length > 0 && (
        <div className="mb-6 p-4 bg-white rounded-lg border border-gray-200">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filter by Portal
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={selectedPortal || portalSearch}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (!selectedPortal) {
                      setPortalSearch(value);
                    } else {
                      // If there's a selected portal, clear it and start searching
                      setSelectedPortal('');
                      setPortalSearch(value);
                    }
                    setIsPortalDropdownOpen(true);
                  }}
                  onFocus={() => {
                    setIsPortalDropdownOpen(true);
                    if (selectedPortal) {
                      setPortalSearch('');
                    }
                  }}
                  onBlur={() => {
                    // Delay closing to allow clicks on dropdown items
                    setTimeout(() => setIsPortalDropdownOpen(false), 150);
                  }}
                  placeholder={selectedPortal ? selectedPortal : `Search portals... (${getUniquePortals().length} total)`}
                  className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {(selectedPortal || portalSearch) && (
                  <button
                    onClick={() => {
                      setSelectedPortal('');
                      setPortalSearch('');
                      setIsPortalDropdownOpen(false);
                    }}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
                
                {/* Dropdown */}
                {isPortalDropdownOpen && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                    {!selectedPortal && !portalSearch && (
                      <button
                        onClick={() => {
                          setSelectedPortal('');
                          setPortalSearch('');
                          setIsPortalDropdownOpen(false);
                        }}
                        className="w-full px-3 py-2 text-left hover:bg-gray-100 text-gray-600"
                      >
                        All Portals ({getUniquePortals().length})
                      </button>
                    )}
                    {getFilteredPortals().map(portal => (
                      <button
                        key={portal}
                        onClick={() => {
                          setSelectedPortal(portal);
                          setPortalSearch('');
                          setIsPortalDropdownOpen(false);
                        }}
                        className="w-full px-3 py-2 text-left hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
                      >
                        {portal}
                      </button>
                    ))}
                    {getFilteredPortals().length === 0 && portalSearch && (
                      <div className="px-3 py-2 text-gray-500 text-sm">
                        No portals match "{portalSearch}"
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search by Username
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={usernameFilter}
                  onChange={(e) => setUsernameFilter(e.target.value)}
                  placeholder="Type to search usernames..."
                  className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {usernameFilter && (
                  <button
                    onClick={() => setUsernameFilter('')}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
            {(selectedPortal || usernameFilter || portalSearch) && (
              <div className="flex items-end">
                <button
                  onClick={() => {
                    setSelectedPortal('');
                    setUsernameFilter('');
                    setPortalSearch('');
                    setIsPortalDropdownOpen(false);
                  }}
                  className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                >
                  Clear Filters
                </button>
              </div>
            )}
          </div>
                               {(selectedPortal || usernameFilter || portalSearch) && (
            <div className="mt-3 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
              <div className="text-sm text-gray-600">
                Showing {Object.keys(getFilteredMfaCodes()).length} of {Object.keys(mfaCodes).length} MFA codes
                {selectedPortal && <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">Portal: {selectedPortal}</span>}
                {portalSearch && !selectedPortal && <span className="ml-2 px-2 py-1 bg-yellow-100 text-yellow-700 rounded text-xs">Searching: "{portalSearch}"</span>}
                {usernameFilter && <span className="ml-2 px-2 py-1 bg-green-100 text-green-700 rounded text-xs">Username: "{usernameFilter}"</span>}
              </div>
              <div className="text-xs text-gray-400">
                Press <kbd className="px-1 py-0.5 bg-gray-100 rounded text-xs">Esc</kbd> to clear filters
              </div>
            </div>
          )}
        </div>
      )}

      <div className="grid gap-3 sm:gap-4">
        {Object.entries(getFilteredMfaCodes()).map(([key, data]) => {
          // Use local countdown for smooth animation, fallback to API data
          const localData = localCountdowns[key] || data;
          const secondsRemaining = localData.seconds_remaining;
          
          return (
            <div key={key} className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-3 sm:space-y-0">
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 text-lg">{localData.portal_name || data.portal_name}</h3>
                  <p className="text-sm text-gray-500">Username: {localData.username || data.username || 'N/A'}</p>
                </div>
                <div className="flex items-center justify-between sm:flex-col sm:items-end sm:text-right">
                  <div className="text-3xl sm:text-2xl font-mono font-bold text-gray-900 mb-0 sm:mb-1">
                    {localData.code || data.code}
                  </div>
                  <div className="flex items-center space-x-3 sm:space-x-2">
                    <div className="text-sm sm:text-sm text-gray-500">
                      {formatTimeRemaining(secondsRemaining)}
                    </div>
                    <div className="w-10 h-10 sm:w-8 sm:h-8 relative">
                      <svg className="w-10 h-10 sm:w-8 sm:h-8 transform -rotate-90">
                        <circle
                          cx="20"
                          cy="20"
                          r="18"
                          stroke="currentColor"
                          strokeWidth="2"
                          fill="none"
                          className="text-gray-200 sm:hidden"
                        />
                        <circle
                          cx="20"
                          cy="20"
                          r="18"
                          stroke="currentColor"
                          strokeWidth="2"
                          fill="none"
                          strokeDasharray={`${2 * Math.PI * 18}`}
                          strokeDashoffset={`${2 * Math.PI * 18 * (1 - getProgressPercentage(secondsRemaining) / 100)}`}
                          className={`${secondsRemaining <= 10 ? 'text-red-500' : 'text-blue-500'} transition-all duration-1000 sm:hidden`}
                          strokeLinecap="round"
                        />
                        {/* Small version for desktop */}
                        <circle
                          cx="16"
                          cy="16"
                          r="14"
                          stroke="currentColor"
                          strokeWidth="2"
                          fill="none"
                          className="text-gray-200 hidden sm:block"
                        />
                        <circle
                          cx="16"
                          cy="16"
                          r="14"
                          stroke="currentColor"
                          strokeWidth="2"
                          fill="none"
                          strokeDasharray={`${2 * Math.PI * 14}`}
                          strokeDashoffset={`${2 * Math.PI * 14 * (1 - getProgressPercentage(secondsRemaining) / 100)}`}
                          className={`${secondsRemaining <= 10 ? 'text-red-500' : 'text-blue-500'} transition-all duration-1000 hidden sm:block`}
                          strokeLinecap="round"
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        {Object.keys(mfaCodes).length === 0 && !isLoading && (
          <div className="text-center py-12 px-4">
            <div className="mb-4">
              <svg className="w-16 h-16 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <p className="text-gray-500 text-base sm:text-lg">
              No MFA codes available yet
            </p>
            <p className="text-gray-400 text-sm mt-2">
              Add your first authenticator using the button above
            </p>
          </div>
        )}

        {Object.keys(mfaCodes).length > 0 && Object.keys(getFilteredMfaCodes()).length === 0 && (
          <div className="text-center py-12 px-4">
            <div className="mb-4">
              <svg className="w-16 h-16 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <p className="text-gray-500 text-base sm:text-lg">
              No MFA codes match your filters
            </p>
            <p className="text-gray-400 text-sm mt-2">
              Try adjusting your portal or username filters
            </p>
            <button
              onClick={() => {
                setSelectedPortal('');
                setUsernameFilter('');
                setPortalSearch('');
                setIsPortalDropdownOpen(false);
              }}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              Clear All Filters
            </button>
          </div>
        )}
      </div>

      {/* Add MFA Modal */}
      {isAddModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-4 sm:p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-gray-900">Add New MFA</h2>
                <button
                  onClick={() => {
                    setIsAddModalOpen(false);
                    stopQrScan();
                    setNewMfa({ name: '', username: '', otp_url: '', otp_code: '' });
                    setActiveTab('qr');
                    setError(null);
                    setShowQrConfirmation(false);
                    setIsProcessingFile(false);
                    setDragActive(false);
                    setPasteActive(false);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Tab Navigation */}
              <div className="flex mb-4 border-b border-gray-200">
                <button
                  onClick={() => {
                    setActiveTab('qr');
                    stopQrScan();
                    setShowQrConfirmation(false);
                    setIsProcessingFile(false);
                    setDragActive(false);
                    setPasteActive(false);
                  }}
                  className={`px-4 py-2 font-medium ${
                    activeTab === 'qr'
                      ? 'text-blue-600 border-b-2 border-blue-600'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Scan QR Code
                </button>
                <button
                  onClick={() => {
                    setActiveTab('manual');
                    stopQrScan();
                    setShowQrConfirmation(false);
                    setIsProcessingFile(false);
                    setDragActive(false);
                    setPasteActive(false);
                  }}
                  className={`px-4 py-2 font-medium ${
                    activeTab === 'manual'
                      ? 'text-blue-600 border-b-2 border-blue-600'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Manual Entry
                </button>
              </div>

              {/* QR Code Tab */}
              {activeTab === 'qr' && (
                <div className="space-y-4">
                  {!isScanning ? (
                    <div className="space-y-4">
                      {/* Camera Scanning Option */}
                      <div className="text-center p-4 border border-gray-200 rounded-lg">
                        <div className="mb-4">
                          <svg className="w-12 h-12 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <button
                          onClick={startQrScan}
                          className="w-full px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-center font-medium"
                        >
                          Start Camera
                        </button>
                        <p className="text-sm text-gray-500 mt-2 text-center">
                          Scan QR code with your camera
                        </p>
                      </div>

                      {/* Divider */}
                      <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                          <div className="w-full border-t border-gray-300" />
                        </div>
                        <div className="relative flex justify-center text-sm">
                          <span className="px-2 bg-white text-gray-500">OR</span>
                        </div>
                      </div>

                      {/* File Upload Option */}
                      <div 
                        className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer ${
                          dragActive 
                            ? 'border-blue-500 bg-blue-50' 
                            : pasteActive
                            ? 'border-green-500 bg-green-50'
                            : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                        } ${isProcessingFile ? 'opacity-50 cursor-not-allowed' : ''}`}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        onPaste={handlePaste}
                        tabIndex={0}
                        onFocus={() => setPasteActive(true)}
                        onBlur={() => setPasteActive(false)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            // Trigger file input click
                            const fileInput = e.currentTarget.querySelector('input[type="file"]');
                            fileInput?.click();
                          }
                        }}
                      >
                        <div className="mb-4">
                          {dragActive ? (
                            <svg className="w-12 h-12 mx-auto text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 12l2 2 4-4" />
                            </svg>
                          ) : pasteActive ? (
                            <svg className="w-12 h-12 mx-auto text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                            </svg>
                          ) : (
                            <svg className="w-12 h-12 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                            </svg>
                          )}
                        </div>
                        <div className="space-y-2">
                          <p className="text-sm text-gray-600 font-medium">
                            {dragActive 
                              ? 'Drop your QR code image here' 
                              : pasteActive 
                              ? 'Press Ctrl+V to paste QR code image'
                              : 'Upload or paste QR code image'
                            }
                          </p>
                          <div className="flex flex-col sm:flex-row gap-2 items-center justify-center">
                            <label className="inline-block">
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) {
                                    handleFileUpload(file);
                                  }
                                  // Reset the input
                                  e.target.value = '';
                                }}
                                className="hidden"
                                disabled={isProcessingFile}
                              />
                              <span className="px-4 py-2 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors cursor-pointer text-sm font-medium">
                                {isProcessingFile ? 'Processing...' : 'Choose File'}
                              </span>
                            </label>
                            <span className="text-xs text-gray-400 hidden sm:inline">or</span>
                            <div className="flex items-center gap-1 px-3 py-1 bg-gray-100 rounded-md text-xs text-gray-600">
                              <kbd className="px-1 py-0.5 bg-white rounded text-xs font-mono">Ctrl</kbd>
                              <span>+</span>
                              <kbd className="px-1 py-0.5 bg-white rounded text-xs font-mono">V</kbd>
                              <span className="ml-1">to paste</span>
                            </div>
                          </div>
                          <p className="text-xs text-gray-500">
                            PNG, JPG, GIF up to 10MB
                          </p>
                          <p className="text-xs text-gray-400">
                            Right-click any QR code → "Copy Image" → Paste here!
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="relative">
                        <Webcam
                          ref={webcamRef}
                          audio={false}
                          className="w-full rounded-lg aspect-square object-cover"
                          screenshotFormat="image/jpeg"
                          videoConstraints={{
                            width: { min: 480, ideal: 1280, max: 1920 },
                            height: { min: 480, ideal: 1280, max: 1920 },
                            facingMode: { ideal: "environment" },
                            aspectRatio: 1.0
                          }}
                          onUserMedia={() => {
                            console.log('Camera stream started');
                          }}
                          onUserMediaError={(error) => {
                            console.error('Camera error:', error);
                            setError('Failed to access camera. Please check permissions.');
                            setIsScanning(false);
                          }}
                        />
                        <div className="absolute inset-0 border-2 border-blue-500 rounded-lg pointer-events-none">
                          <div className="absolute top-2 sm:top-4 left-2 sm:left-4 w-6 h-6 sm:w-8 sm:h-8 border-t-4 border-l-4 border-blue-500"></div>
                          <div className="absolute top-2 sm:top-4 right-2 sm:right-4 w-6 h-6 sm:w-8 sm:h-8 border-t-4 border-r-4 border-blue-500"></div>
                          <div className="absolute bottom-2 sm:bottom-4 left-2 sm:left-4 w-6 h-6 sm:w-8 sm:h-8 border-b-4 border-l-4 border-blue-500"></div>
                          <div className="absolute bottom-2 sm:bottom-4 right-2 sm:right-4 w-6 h-6 sm:w-8 sm:h-8 border-b-4 border-r-4 border-blue-500"></div>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <button
                          onClick={stopQrScan}
                          className="w-full px-4 py-3 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors text-center"
                        >
                          Stop Scanning
                        </button>
                        <div className="text-center text-sm text-gray-500">
                          Hold a QR code steady in view of the camera
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => {
                              // Test with a problematic OTP URL that used to crash the system
                              const testUrl = "otpauth://totp/Availity:mferrede?secret=QBJNPB7TOBTQQNQY242TXWNJW56RF6VRBOFQOPSORJPQOGOFFYOG2UIMG5FP3WSK&period=30&digits=6&b=032b75";
                              console.log('Testing with problematic QR data (should now work):', testUrl);
                              handleQrResult(testUrl);
                            }}
                            className="flex-1 px-4 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600 transition-colors text-center text-xs"
                          >
                            Test Problem URL
                          </button>
                          <button
                            onClick={async () => {
                              console.log('Trying simple scanner restart...');
                              stopQrScan();
                              setIsScanning(true);
                              
                              try {
                                await new Promise(resolve => setTimeout(resolve, 500));
                                
                                if (webcamRef.current && webcamRef.current.video) {
                                  const video = webcamRef.current.video;
                                  
                                  // Simple scanner without fancy options
                                  scannerRef.current = new QrScanner(video, (result) => {
                                    console.log('Simple QR detected:', result);
                                    handleQrResult(result);
                                  });
                                  
                                  await scannerRef.current.start();
                                  console.log('Simple QR scanner started');
                                }
                              } catch (error) {
                                console.error('Simple scanner failed:', error);
                                setError(`Simple scanner failed: ${error.message}`);
                                setIsScanning(false);
                              }
                            }}
                            className="flex-1 px-4 py-2 bg-purple-500 text-white rounded-md hover:bg-purple-600 transition-colors text-center text-xs"
                          >
                            Simple Scan
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Form fields for QR tab */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Portal Name
                      </label>
                      <input
                        type="text"
                        value={newMfa.name}
                        onChange={(e) => {
                          setNewMfa({...newMfa, name: e.target.value});
                          if (showQrConfirmation) setShowQrConfirmation(false);
                        }}
                        className="w-full px-4 py-3 text-base border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., Google, GitHub, etc."
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Username
                      </label>
                      <input
                        type="text"
                        value={newMfa.username}
                        onChange={(e) => {
                          setNewMfa({...newMfa, username: e.target.value});
                          if (showQrConfirmation) setShowQrConfirmation(false);
                        }}
                        className="w-full px-4 py-3 text-base border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Your username"
                        required
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Manual Entry Tab */}
              {activeTab === 'manual' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Portal Name
                    </label>
                    <input
                      type="text"
                      value={newMfa.name}
                      onChange={(e) => {
                        setNewMfa({...newMfa, name: e.target.value});
                        if (showQrConfirmation) setShowQrConfirmation(false);
                      }}
                      className="w-full px-4 py-3 text-base border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., Google, GitHub, etc."
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Username
                    </label>
                    <input
                      type="text"
                      value={newMfa.username}
                      onChange={(e) => {
                        setNewMfa({...newMfa, username: e.target.value});
                        if (showQrConfirmation) setShowQrConfirmation(false);
                      }}
                      className="w-full px-4 py-3 text-base border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Your username"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Secret Key or OTP URL
                    </label>
                    <textarea
                      value={newMfa.otp_url || newMfa.otp_code}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value.startsWith('otpauth://')) {
                          setNewMfa({...newMfa, otp_url: value, otp_code: ''});
                        } else {
                          setNewMfa({...newMfa, otp_code: value, otp_url: ''});
                        }
                        if (showQrConfirmation) setShowQrConfirmation(false);
                      }}
                      className="w-full px-4 py-3 text-base border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                      placeholder="Paste secret key or full OTP URL (otpauth://...)"
                      rows={4}
                      required
                    />
                    <p className="text-sm text-gray-500 mt-2">
                      Enter either the secret key or paste the full OTP URL
                    </p>
                  </div>
                </div>
              )}

              {/* QR Code Confirmation */}
              {showQrConfirmation && (
                <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-md">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <svg className="w-5 h-5 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-amber-800">
                        📋 QR Code Scanned Successfully
                      </h3>
                      <div className="mt-2 text-sm text-amber-700">
                        <p>Auto-filled from QR code. You can edit these details if needed or submit as-is:</p>
                        <div className="mt-2 space-y-1">
                          <p><strong>Portal:</strong> {newMfa.name}</p>
                          <p><strong>Username:</strong> {newMfa.username}</p>
                        </div>
                      </div>
                      <div className="mt-3">
                        <button
                          onClick={() => setShowQrConfirmation(false)}
                          className="text-sm bg-amber-100 px-3 py-1 rounded-md text-amber-800 hover:bg-amber-200 transition-colors"
                        >
                          Dismiss
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row sm:justify-end space-y-3 sm:space-y-0 sm:space-x-3 mt-6">
                <button
                  onClick={() => {
                    setIsAddModalOpen(false);
                    stopQrScan();
                    setNewMfa({ name: '', username: '', otp_url: '', otp_code: '' });
                    setActiveTab('qr');
                    setError(null);
                    setShowQrConfirmation(false);
                    setIsProcessingFile(false);
                    setDragActive(false);
                    setPasteActive(false);
                  }}
                  className="w-full sm:w-auto px-4 py-3 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors text-center"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddMfa}
                  disabled={isSubmitting || !newMfa.name || !newMfa.username || (!newMfa.otp_url && !newMfa.otp_code)}
                  className="w-full sm:w-auto px-4 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-center"
                >
                  {isSubmitting ? 'Adding...' : 'Add MFA'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MfaAuthenticator; 